import { createContext, useContext, useState, useEffect, useCallback } from "react";
import axiosClient from "../api/axiosClient";

const CartContext = createContext(null);

export const CartProvider = ({ children }) => {
  const [cartItems, setCartItems] = useState([]);
  const [cartOpen, setCartOpen] = useState(false);
  const [cartLoading, setCartLoading] = useState(false);
  // Track which item IDs are currently being deleted
  const [removingIds, setRemovingIds] = useState(new Set());

  /* ── normalise a DB document into the shape the UI expects ── */
  const normalise = (doc) => ({
    id:                   doc._id,
    bikeId:               doc.bikeId?._id ?? doc.bikeId,
    bikeName:             doc.bikeId?.name        ?? "",
    bikeImage:            doc.bikeId?.image       ?? "",
    bikeType:             doc.bikeId?.type        ?? "",
    startDate:            doc.startDate,
    endDate:              doc.endDate,
    pricePerDay:          doc.pricePerDay,
    effectivePricePerDay: doc.effectivePricePerDay,
    totalAmount:          doc.totalAmount,
  });

  /* ── fetch cart from DB (called on mount + after login) ── */
  const fetchCart = useCallback(async () => {
    const token = localStorage.getItem("token");
    if (!token) { setCartItems([]); return; }

    try {
      setCartLoading(true);
      const res = await axiosClient.get("/cart");
      setCartItems((res.data.items || []).map(normalise));
    } catch {
      setCartItems([]);
    } finally {
      setCartLoading(false);
    }
  }, []);

  // ── React to login / logout by watching the token in localStorage ──
  // When a new token appears (login) → fetch that user's cart.
  // When the token is removed (logout) → immediately wipe the local cart.
  useEffect(() => {
    fetchCart(); // initial load

    const handleStorageChange = (e) => {
      if (e.key === "token") {
        if (e.newValue) {
          // A user just logged in — load their cart
          fetchCart();
        } else {
          // A user just logged out — wipe cart & close drawer
          setCartItems([]);
          setCartOpen(false);
        }
      }
    };

    window.addEventListener("storage", handleStorageChange);
    return () => window.removeEventListener("storage", handleStorageChange);
  }, [fetchCart]);

  /* ── add item ── */
  const addToCart = async (item) => {
    try {
      const res = await axiosClient.post("/cart", {
        bikeId:               item.bikeId,
        startDate:            item.startDate,
        endDate:              item.endDate,
        pricePerDay:          item.pricePerDay,
        effectivePricePerDay: item.effectivePricePerDay,
        totalAmount:          item.totalAmount,
      });
      // Re-fetch so populated bikeId is included
      await fetchCart();
      return res.data.item;
    } catch (err) {
      console.error("Cart add error:", err.response?.data || err.message);
      throw err;
    }
  };

  /* ── remove one item ──
     Only remove from local state AFTER the server confirms deletion.
     Uses a per-item removingIds set so the delete button shows a spinner
     independently of any "proceed" loading state.                        */
  const removeFromCart = async (id) => {
    setRemovingIds((prev) => new Set([...prev, id]));
    try {
      await axiosClient.delete(`/cart/${id}`);
      // Server confirmed — now remove from local state
      setCartItems((prev) => prev.filter((i) => i.id !== id));
    } catch (err) {
      console.error("Cart remove error:", err.response?.data || err.message);
    } finally {
      setRemovingIds((prev) => {
        const next = new Set(prev);
        next.delete(id);
        return next;
      });
    }
  };

  /* ── clear entire cart ── */
  const clearCart = async () => {
    try {
      await axiosClient.delete("/cart");
      setCartItems([]);
    } catch (err) {
      console.error("Cart clear error:", err.response?.data || err.message);
    }
  };

  /* ── expose a helper so the drawer knows which items are being removed ── */
  const isRemoving = (id) => removingIds.has(id);

  /* ── check if a bikeId+dates combo is already in cart ── */
  const isInCart = (cartItemId) => {
    // cartItemId format: `${bikeId}-${startDate}-${endDate}`
    const [bikeId, ...rest] = cartItemId.split("-");
    const startDate = rest.slice(0, rest.length / 2).join("-");
    const endDate   = rest.slice(rest.length / 2).join("-");
    return cartItems.some(
      (i) =>
        (i.bikeId === bikeId || String(i.bikeId) === bikeId) &&
        new Date(i.startDate).toISOString() === new Date(startDate).toISOString() &&
        new Date(i.endDate).toISOString()   === new Date(endDate).toISOString()
    );
  };

  return (
    <CartContext.Provider
      value={{
        cartItems,
        cartLoading,
        cartOpen,
        setCartOpen,
        fetchCart,
        addToCart,
        removeFromCart,
        clearCart,
        isInCart,
        isRemoving,
      }}
    >
      {children}
    </CartContext.Provider>
  );
};

export const useCart = () => useContext(CartContext);
